import { useState } from "react";
import { Copy, ArrowRight, Download, Check, Github } from "lucide-react";

/**
 * Provably Fair section — landing page social-proof strip.
 *
 * Visual is straight off the design handoff (Hupper-Pong bundle,
 * "Provably Fair Section.html"): pulsing-dot eyebrow, headline with
 * an italic serif accent word, a single unified info strip with
 * three columns separated by hairline dividers (NOT a `gap-px` grid
 * — the dividers are vertical lines inside one card), and a copy-
 * to-clipboard affordance with green-check feedback.
 *
 * Real on-chain data: Hupper escrow program on Solana mainnet. The
 * address + Solscan URL mirror src/contracts/solana/config.ts
 * (HUPPER_ESCROW_PROGRAM_ID) — if the program moves, only this
 * constants block changes.
 */

// Hupper escrow program on Solana mainnet. Source of truth:
// src/contracts/solana/config.ts (HUPPER_ESCROW_PROGRAM_ID).
const CONTRACT_ADDRESS = "RAW7dgEuw7NUs8fXd1LgvNddGr1d85Yv8959ATVUwYw";
const SOLSCAN_URL = `https://solscan.io/account/${CONTRACT_ADDRESS}`;
const SHORT_ADDR = `${CONTRACT_ADDRESS.slice(0, 6)}…${CONTRACT_ADDRESS.slice(-4)}`;
const GITHUB_URL = "https://github.com/leoarx/Hupper";

export default function ProvablyFair() {
  const [copied, setCopied] = useState(false);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(CONTRACT_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className="relative bg-[var(--color-canvas)]">
      {/* Soft orange radial glow in the top-right, like the design's
          body::before. Pointer-events none so it never intercepts. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 50% 40% at 80% 30%, rgba(255, 107, 0, 0.05), transparent 60%)",
        }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-30">
        {/* Header row — h1 on the left (1fr), lead paragraph on the right
            (fixed 360px column at md+) — matches the design's 1fr / 360px
            grid. Both columns share the same baseline at the bottom. */}
        <div className="grid md:grid-cols-[1fr_360px] gap-10 md:gap-12 items-end mb-10">
          <div>
            <div
              className="inline-flex items-center gap-2.5 px-3 py-[5px] rounded-full mb-4 font-mono text-[11px] font-semibold tracking-[0.16em]"
              style={{
                border: "1px solid var(--color-line-emphasis)",
                background: "rgba(28, 24, 20, 0.6)",
                color: "var(--color-brand)",
              }}
            >
              <span className="relative inline-flex h-1.5 w-1.5">
                <span
                  className="absolute inline-flex h-full w-full rounded-full animate-ping opacity-60"
                  style={{ background: "var(--color-brand)" }}
                />
                <span
                  className="relative inline-flex h-1.5 w-1.5 rounded-full"
                  style={{
                    background: "var(--color-brand)",
                    boxShadow: "0 0 0 3px rgba(255, 107, 0, 0.2)",
                  }}
                />
              </span>
              PROVABLY FAIR
            </div>
            <h2
              className="font-display font-bold text-[var(--color-text-primary)] m-0"
              style={{
                fontSize: "clamp(40px, 4.4vw, 60px)",
                lineHeight: 1,
                letterSpacing: "-2.2px",
                textWrap: "balance",
              }}
            >
              Every match settled
              <br />
              by an audited{" "}
              <span
                className="font-['Instrument_Serif'] italic font-normal"
                style={{ color: "var(--color-brand)" }}
              >
                contract.
              </span>
            </h2>
          </div>
          <p className="text-[14.5px] text-[var(--color-text-tertiary)] leading-[1.55] m-0">
            Match outcomes are signed and submitted on-chain. The escrow
            contract is{" "}
            <b className="text-[var(--color-text-primary)] font-medium">
              immutable, audited, and open-source
            </b>
            .
          </p>
        </div>

        {/* Unified strip — single card with internal vertical dividers.
            grid-template-columns 1.1fr 1fr 1fr matches the design's
            slightly wider first cell (the address is the longest value
            and earns the extra breathing room). */}
        <div
          className="grid md:grid-cols-[1.1fr_1fr_1fr] rounded-[14px] overflow-hidden"
          style={{
            border: "1px solid var(--color-line-emphasis)",
            background:
              "linear-gradient(180deg, rgba(28, 24, 20, 0.7), rgba(14, 11, 9, 0.85))",
          }}
        >
          {/* ── ESCROW CONTRACT ─────────────────────────────────────── */}
          <Block divider>
            <BlockLabel
              label="ESCROW CONTRACT"
              meta={<span className="text-[var(--color-text-tertiary)]">SOLANA</span>}
            />
            <div className="flex items-center gap-2.5 font-mono text-[16px] font-semibold text-[var(--color-text-primary)] tracking-[-0.2px]">
              <span>{SHORT_ADDR}</span>
              <button
                onClick={copyAddress}
                title="Copy address"
                aria-label="Copy contract address"
                className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-[5px] transition-all duration-150 cursor-pointer"
                style={{
                  border: `1px solid ${copied ? "var(--color-live)" : "var(--color-line-emphasis)"}`,
                  background: copied ? "rgba(52, 211, 153, 0.08)" : "transparent",
                  color: copied ? "var(--color-live)" : "var(--color-text-tertiary)",
                }}
              >
                {copied ? (
                  <Check className="w-[11px] h-[11px]" strokeWidth={2.5} />
                ) : (
                  <Copy className="w-[11px] h-[11px]" strokeWidth={2} />
                )}
              </button>
            </div>
            <BlockLink href={SOLSCAN_URL} external>
              View on Solscan
            </BlockLink>
          </Block>

          {/* ── SECURITY AUDIT ──────────────────────────────────────── */}
          <Block divider>
            <BlockLabel
              label="SECURITY AUDIT"
              meta={
                <span className="inline-flex items-center gap-1.5 text-[var(--color-live)]">
                  <span
                    className="inline-block w-[5px] h-[5px] rounded-full"
                    style={{
                      background: "var(--color-live)",
                      boxShadow: "0 0 0 2px rgba(52, 211, 153, 0.2)",
                    }}
                  />
                  PASSED
                </span>
              }
            />
            <div className="font-mono text-[16px] font-semibold text-[var(--color-text-primary)] tracking-[-0.2px]">
              OpenZeppelin
            </div>
            <BlockLink href="#">
              <Download className="w-[11px] h-[11px]" strokeWidth={2} />
              Download report
            </BlockLink>
          </Block>

          {/* ── OPEN SOURCE ─────────────────────────────────────────── */}
          <Block>
            <BlockLabel
              label="OPEN SOURCE"
              meta={<span className="text-[var(--color-text-tertiary)]">MIT</span>}
            />
            <div className="inline-flex items-center gap-2 font-mono text-[16px] font-semibold text-[var(--color-text-primary)] tracking-[-0.2px]">
              <Github className="w-[14px] h-[14px]" strokeWidth={0} fill="currentColor" />
              hupper/contracts
            </div>
            <BlockLink href={GITHUB_URL} external>
              Browse on GitHub
            </BlockLink>
          </Block>
        </div>
      </div>
    </section>
  );
}

// ── Sub-components ────────────────────────────────────────────────────
// Kept inline because they're only meaningful in the context of this
// section's specific layout (gap, padding, divider behavior).

function Block({
  children,
  divider = false,
}: {
  children: React.ReactNode;
  divider?: boolean;
}) {
  return (
    <div
      className="flex flex-col gap-3 px-6 md:px-[26px] py-[22px] relative"
      style={
        divider
          ? { borderRight: "1px solid var(--color-line)" }
          : undefined
      }
    >
      {children}
    </div>
  );
}

function BlockLabel({ label, meta }: { label: string; meta: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono text-[10.5px] font-semibold tracking-[0.16em] text-[var(--color-text-tertiary)]">
        {label}
      </span>
      <span className="font-mono text-[10px] tracking-[0.12em]">{meta}</span>
    </div>
  );
}

function BlockLink({
  href,
  external = false,
  children,
}: {
  href: string;
  external?: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="group inline-flex items-center gap-1.5 mt-auto self-start font-mono text-[12px] text-[var(--color-text-tertiary)] hover:text-[var(--color-brand)] transition-colors duration-150 no-underline"
    >
      {children}
      <ArrowRight
        className="w-[11px] h-[11px] transition-transform duration-150 group-hover:translate-x-0.5"
        strokeWidth={2}
      />
    </a>
  );
}
